export default function () {
    const store = useDefaultStore();
    const route = useRoute();
    const fsStore = useFunctionsheetStore();
    const executedFunctions = ref(new Set<string>());

    const isFS = route.name.includes("kitchen");

    // Computed refs for better performance
    const hasRequiredOrderData = computed(() => {
        if (!store.orderData?.[store.curShop.url]) return false;

        if (fsStore.page == "day") {
            return !!store.orderData[store.curShop.url][fsStore.date];
        } else {
            return fsStore.days.every(
                (date) => !!store.orderData[store.curShop.url][date],
            );
        }
    });

    if (isFS) {
        // Watcher to initialize cells when order data is ready
        watch(
            () => hasRequiredOrderData.value,
            (hasData) => {
                if (hasData && !executedFunctions.value.has("initCells")) {
                    console.log("Order data ready, initializing cells");
                    fsStore.initCells();
                    executedFunctions.value.add("initCells");
                }
            },
        );

        // Watcher to reinit cells when order data updates
        watch(
            () => fsStore.updatedOrderDataProcessed,
            (updatedOrderDataProcessed) => {
                if (updatedOrderDataProcessed) {
                    console.log("Order updated, reinitializing cells");
                    store.processing = true;
                    fsStore.initCells();
                    store.processing = false;
                }
            },
        );
    }

    // The calendar watcher is no longer needed as its only job was to trigger
    // data processing, which is now handled reliably by initData.

    async function orchestrateOrderFetch(options) {
        const { getDates, getFetchId, setFetchId, setProcessing, setLoading } =
            options;

        const newFetchId = getFetchId() + 1;
        setFetchId(newFetchId);
        const localFetchId = newFetchId;

        console.log("Orchestrating order fetch...");

        // Part 1: Show cached data first
        setProcessing(true);

        let dateISOs = getDates();
        const cachedOrdersCount = await store.hydrateOrderData(dateISOs);

        if (cachedOrdersCount === 0) {
            setLoading(true);
        }

        // Part 2: Fetch fresh data
        await initData();

        if (getFetchId() !== localFetchId) {
            return; // Stale request, so we don't proceed.
        }

        // Part 3: Refresh with fresh data

        // Re-evaluate date range in case it changed during await
        dateISOs = getDates();
        await store.hydrateOrderData(dateISOs);

        setProcessing(false);
        setLoading(false);
    }

    async function initData() {
        // This function now orchestrates the entire data loading and processing flow.
        // It ensures that data is fetched AND processed before resolving.

        // 1. Start fetches in parallel
        const fetchProductsPromise = store.productDataProcessed
            ? Promise.resolve()
            : store.fetchProducts();
        const fetchOrdersPromise = store.fetchOrders();

        // 2. Wait for both fetches to complete
        await Promise.all([fetchProductsPromise, fetchOrdersPromise]);

        // 3. Process products if needed (this is synchronous)
        if (!store.productDataProcessed) {
            store.processProductData();
        }

        // 4. Process orders (this is asynchronous and depends on products being processed)
        await store.processOrderData();
    }

    return {
        initData,
        orchestrateOrderFetch,
    };
}
